/** Binary media transport is separate from JSON and requires no DOM runtime.
 * The injected transport owns cookie/Bearer authentication and cancellation.
 * It must stream media; it must not buffer a recording to authenticate it.
 */
import { pinnedScope, scopeQuery } from './coachingScope';
import type { CoachingScopeIntent } from './coachingScope';
import type { components, paths } from './api.generated';

type Pin = { resourceId: string; revisionId: string };
type Child = { feedbackId: string; playerId: string; view: 'coach' | 'recipient' };
type RecordingQuery = NonNullable<paths['/api/coach/resources/recording-source/media']['get']['parameters']['query']>;
export type ResourceRecordingMediaSelection = {
  grant: 'recording'; recordingId: string; recordingGeneration: string;
  matchId: string; slot: RecordingQuery['slot'];
};
export type ResourceFilmstripWindow = { start: number; end: number; frames?: number };
export type ResourceMediaSelection = ResourceRecordingMediaSelection | (Pin & (
  | { grant: 'coach' }
  | (Child & { grant: 'current'; expectedFeedbackRevisionId: string })
  | (Child & { grant: 'answered'; messageId: string })
));
export type ResourceMediaError = components['schemas']['ResourceMediaErrorResponse'];
export type ResourceMediaRequest = {
  method: 'GET' | 'HEAD'; path: string; query: Record<string, string>; headers: Record<string, string>;
};
export interface ResourceMediaResponse {
  status: number;
  headers: Readonly<Record<string, string>>;
  body: AsyncIterable<Uint8Array> | null;
  close(): Promise<void>;
}
export interface ResourceMediaTransport {
  request(request: ResourceMediaRequest): Promise<ResourceMediaResponse>;
}

function uuid(value: string): string {
  if (typeof value !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    throw new RangeError('invalid_resource_media_id');
  }
  return encodeURIComponent(value.toLowerCase());
}

/** Closed selected identities; no latest revision, arbitrary URL or credential. */
export function resourceMediaRequest(intent: CoachingScopeIntent, selection: ResourceMediaSelection,
  kind: 'media' | 'thumbnail' | 'filmstrip', method: 'GET' | 'HEAD' = 'GET', range?: string,
  window?: ResourceFilmstripWindow): ResourceMediaRequest {
  if (!['media', 'thumbnail', 'filmstrip'].includes(kind) || !['GET', 'HEAD'].includes(method)
    || (kind !== 'media' && (method !== 'GET' || range !== undefined))
    || (kind !== 'filmstrip' && window !== undefined)) throw new RangeError('invalid_resource_media_operation');
  const query = scopeQuery(pinnedScope(intent));
  let path = '/api/coach';
  if (selection.grant === 'recording') {
    if (kind === 'thumbnail' || !selection.matchId || selection.matchId.length > 120
      || !selection.recordingGeneration || selection.recordingGeneration.length > 128
      || !['full', 'first_half', 'second_half', 'extra_time', 'penalty_shootout'].includes(selection.slot)) {
      throw new RangeError('invalid_resource_recording_target');
    }
    query.recording_id = uuid(selection.recordingId);
    query.recording_generation = selection.recordingGeneration;
    query.match_id = selection.matchId; query.slot = selection.slot;
    path += '/resources/recording-source';
  } else {
    if (selection.grant !== 'coach') {
      if (!['current', 'answered'].includes(selection.grant) || !['coach', 'recipient'].includes(selection.view)
        || !selection.playerId || selection.playerId.length > 200) throw new RangeError('invalid_resource_media_target');
      path = `/api/feedback/${uuid(selection.feedbackId)}/players/${encodeURIComponent(selection.playerId)}/conversation`;
      query.view = selection.view;
      if (selection.grant === 'current') query.expected_feedback_revision_id = uuid(selection.expectedFeedbackRevisionId);
      else path += `/messages/${uuid(selection.messageId)}/context`;
    }
    path += `/resources/${uuid(selection.resourceId)}/revisions/${uuid(selection.revisionId)}`;
  }
  if (kind === 'filmstrip') {
    if (!['coach', 'recording'].includes(selection.grant) || !window
      || !Number.isFinite(window.start) || !Number.isFinite(window.end)
      || window.start < 0 || window.end > 21600 || window.end <= window.start
      || !Number.isInteger(window.frames ?? 12) || (window.frames ?? 12) < 4 || (window.frames ?? 12) > 16) {
      throw new RangeError('invalid_resource_filmstrip_window');
    }
    query.start = String(window.start); query.end = String(window.end); query.frames = String(window.frames ?? 12);
  }
  path += `/${kind}`;
  if (range !== undefined && (!/^bytes=/.test(range) || /[\r\n]/.test(range) || range.length > 4096)) {
    throw new RangeError('invalid_resource_media_range');
  }
  return { method, path, query, headers: range === undefined ? {} : { Range: range } };
}

/** Caller captures one actor/scope/permission lifetime, not a reusable login ID.
 * The guard must remain false after revoke/restore; every returned byte checks
 * it. A consumer closes early-abandoned bodies and disposes its guard separately.
 */
export function resourceMediaClient(transport: ResourceMediaTransport, intent: CoachingScopeIntent,
  current: () => boolean) {
  const captured: CoachingScopeIntent = { team_id: intent.team_id, season: { ...intent.season } };
  const assertCurrent = () => { if (!current()) throw new Error('resource_media_context_changed'); };
  async function request(selection: ResourceMediaSelection, kind: 'media' | 'thumbnail' | 'filmstrip', method: 'GET' | 'HEAD', range?: string,
    window?: ResourceFilmstripWindow) {
    assertCurrent();
    const response = await transport.request(resourceMediaRequest(captured, selection, kind, method, range, window))
      .catch(error => { assertCurrent(); throw error; });
    let closing: Promise<void> | undefined;
    async function close() {
      try { await (closing ??= response.close()); } catch (error) { assertCurrent(); throw error; }
    }
    try { assertCurrent(); } catch (error) { await close(); throw error; }
    const body = response.body;
    return { status: response.status, headers: response.headers, close,
      body: body === null ? null : (async function* () {
        try {
          assertCurrent();
          for await (const chunk of body) { assertCurrent(); yield chunk; }
          assertCurrent();
        } catch (error) { assertCurrent(); throw error; }
        finally { await close(); }
      })() } satisfies ResourceMediaResponse;
  }
  return {
    media: (selection: ResourceMediaSelection, range?: string) => request(selection, 'media', 'GET', range),
    head: (selection: ResourceMediaSelection, range?: string) => request(selection, 'media', 'HEAD', range),
    thumbnail: (selection: ResourceMediaSelection) => request(selection, 'thumbnail', 'GET'),
    filmstrip: (selection: ResourceMediaSelection, window: ResourceFilmstripWindow) => request(selection, 'filmstrip', 'GET', undefined, window),
  };
}
